import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import type { PersistenceBlob } from '../hooks/usePersistence';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Times-Roman',
    fontSize: 12,
    paddingTop: 72, // 1 inch
    paddingBottom: 72,
    paddingLeft: 108, // 1.5 inch (bound)
    paddingRight: 72, // 1 inch
    lineHeight: 1.0,
  },
  // Cover page
  coverPage: {
    fontFamily: 'Times-Roman',
    fontSize: 12,
    paddingTop: 72,
    paddingBottom: 72,
    paddingLeft: 108,
    paddingRight: 72,
    justifyContent: 'center',
    alignItems: 'center',
  },
  coverTitle: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 24,
    textTransform: 'uppercase',
  },
  coverByline: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
  },
  coverAuthor: {
    fontSize: 14,
    textAlign: 'center',
  },
  coverBottom: {
    position: 'absolute',
    bottom: 72,
    left: 108,
    right: 72,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  coverCopyright: {
    fontSize: 10,
  },
  coverContact: {
    fontSize: 10,
    textAlign: 'right',
  },
  // Cast page
  castTitle: {
    fontSize: 14,
    textAlign: 'center',
    textTransform: 'uppercase',
    marginBottom: 24,
    textDecoration: 'underline',
  },
  castEntry: {
    fontSize: 12,
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  // Script elements
  characterName: {
    textAlign: 'center',
    textTransform: 'uppercase',
    marginTop: 18,
    marginBottom: 0,
  },
  dialogue: {
    textAlign: 'left',
    paddingLeft: 72,
    paddingRight: 72,
    marginBottom: 0,
  },
  parenthetical: {
    textAlign: 'left',
    fontStyle: 'italic',
    paddingLeft: 144,
    paddingRight: 108,
    marginBottom: 0,
  },
  stageDirection: {
    textAlign: 'left',
    fontStyle: 'italic',
    paddingLeft: 144,
    paddingRight: 108,
    marginTop: 6,
    marginBottom: 6,
  },
  actHeading: {
    textAlign: 'center',
    textTransform: 'uppercase',
    textDecoration: 'underline',
    marginTop: 24,
    marginBottom: 12,
  },
  sceneHeading: {
    textAlign: 'center',
    textDecoration: 'underline',
    marginTop: 12,
    marginBottom: 12,
  },
  pageNumber: {
    position: 'absolute',
    top: 36,
    right: 72,
    fontSize: 10,
    color: '#666',
  },
  endNotation: {
    textAlign: 'center',
    marginTop: 36,
    textTransform: 'uppercase',
    letterSpacing: 2,
  },
});

interface TiptapNode {
  type: string;
  content?: TiptapNode[];
  text?: string;
  marks?: { type: string }[];
}

// Extract plain text from Tiptap node content (handling marks)
function extractText(content?: TiptapNode[]): string {
  if (!content) return '';
  return content
    .map((node) => {
      if (node.type === 'text') return node.text ?? '';
      if (node.content) return extractText(node.content);
      return '';
    })
    .join('');
}

// Render inline content with marks (bold, italic, underline)
function renderInlineContent(content?: TiptapNode[]): React.ReactNode[] {
  if (!content) return [];
  return content.map((node, i) => {
    if (node.type === 'text') {
      let style: any = {};
      if (node.marks) {
        for (const mark of node.marks) {
          if (mark.type === 'bold') style.fontWeight = 'bold';
          if (mark.type === 'italic') style.fontStyle = 'italic';
          if (mark.type === 'underline') style.textDecoration = 'underline';
        }
      }
      return (
        <Text key={i} style={Object.keys(style).length > 0 ? style : undefined}>
          {node.text}
        </Text>
      );
    }
    return null;
  });
}

// Extract unique character names in order of first appearance
function extractCast(doc: TiptapNode): string[] {
  const seen = new Set<string>();
  const cast: string[] = [];

  function walk(node: TiptapNode) {
    if (node.type === 'characterName') {
      const name = extractText(node.content).trim().toUpperCase();
      if (name && !seen.has(name)) {
        seen.add(name);
        cast.push(name);
      }
    }
    if (node.content) node.content.forEach(walk);
  }

  walk(doc);
  return cast;
}

interface PlayDocumentProps {
  blob: PersistenceBlob;
}

function PlayDocument({ blob }: PlayDocumentProps) {
  const doc = blob.document as TiptapNode;
  const cast = extractCast(doc);
  const { title, metadata } = blob;

  return (
    <Document>
      {/* Cover Page */}
      <Page size="LETTER" style={styles.coverPage}>
        <View>
          <Text style={styles.coverTitle}>{title || 'Untitled Play'}</Text>
          <Text style={styles.coverByline}>by</Text>
          <Text style={styles.coverAuthor}>{metadata.author || 'Author Name'}</Text>
        </View>
        <View style={styles.coverBottom}>
          <Text style={styles.coverCopyright}>{metadata.copyright}</Text>
          <View>
            {metadata.contact.email && (
              <Text style={styles.coverContact}>{metadata.contact.email}</Text>
            )}
            {metadata.contact.phone && (
              <Text style={styles.coverContact}>{metadata.contact.phone}</Text>
            )}
            {metadata.contact.address && (
              <Text style={styles.coverContact}>{metadata.contact.address}</Text>
            )}
          </View>
        </View>
      </Page>

      {/* Cast of Characters Page */}
      {cast.length > 0 && (
        <Page size="LETTER" style={styles.page}>
          <Text style={styles.castTitle}>Cast of Characters</Text>
          {cast.map((name, i) => (
            <Text key={i} style={styles.castEntry}>{name}</Text>
          ))}
        </Page>
      )}

      {/* Script Body */}
      <Page size="LETTER" style={styles.page} wrap>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber }) => `${pageNumber}`}
          fixed
        />
        {doc.content?.map((node, i) => {
          switch (node.type) {
            case 'characterName':
              return (
                <Text key={i} style={styles.characterName}>
                  {renderInlineContent(node.content)}
                </Text>
              );
            case 'dialogue':
              return (
                <Text key={i} style={styles.dialogue}>
                  {renderInlineContent(node.content)}
                </Text>
              );
            case 'parenthetical':
              return (
                <Text key={i} style={styles.parenthetical}>
                  ({renderInlineContent(node.content)})
                </Text>
              );
            case 'stageDirection':
              return (
                <Text key={i} style={styles.stageDirection}>
                  ({renderInlineContent(node.content)})
                </Text>
              );
            case 'actHeading':
              return (
                <Text key={i} style={styles.actHeading} break>
                  {renderInlineContent(node.content)}
                </Text>
              );
            case 'sceneHeading':
              return (
                <Text key={i} style={styles.sceneHeading}>
                  {renderInlineContent(node.content)}
                </Text>
              );
            case 'paragraph':
              // Fallback for any plain paragraphs
              return (
                <Text key={i} style={styles.dialogue}>
                  {renderInlineContent(node.content)}
                </Text>
              );
            default:
              return null;
          }
        })}

        {/* End notation */}
        <Text style={styles.endNotation}>End of Play</Text>
      </Page>
    </Document>
  );
}

export async function exportToPdf(blob: PersistenceBlob): Promise<void> {
  const doc = <PlayDocument blob={blob} />;
  const pdfBlob = await pdf(doc).toBlob();
  const filename = `${blob.title || 'Untitled Play'}.pdf`;
  saveAs(pdfBlob, filename);
}

export async function printPdf(blob: PersistenceBlob): Promise<void> {
  const doc = <PlayDocument blob={blob} />;
  const pdfBlob = await pdf(doc).toBlob();
  const url = URL.createObjectURL(pdfBlob);

  // Use a hidden iframe to avoid popup blockers
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);
  iframe.onload = () => {
    iframe.contentWindow?.print();
  };
  // Clean up after a delay
  setTimeout(() => {
    document.body.removeChild(iframe);
    URL.revokeObjectURL(url);
  }, 60000);
}
